export type OAuthAnalyticsMethod = 'oauth_github' | 'oauth_google'

export interface DeferredOAuthStartIntent {
  oauth_state: string
  method: OAuthAnalyticsMethod
  created_at: number
}

const OAUTH_STATE_PATTERN = /^[\w-]{32,128}$/
const STORAGE_KEY = 'spatius_oauth_start_intent'
const MAX_AGE_MS = 15 * 60 * 1000
const FUTURE_TOLERANCE_MS = 60 * 1000

function sessionStorageOrUndefined(): Storage | undefined {
  if (typeof window === 'undefined')
    return undefined
  try {
    return window.sessionStorage
  }
  catch {
    return undefined
  }
}

function readIntent(now = Date.now()): DeferredOAuthStartIntent | undefined {
  const storage = sessionStorageOrUndefined()
  if (!storage)
    return undefined
  try {
    const raw = storage.getItem(STORAGE_KEY)
    if (!raw)
      return undefined
    const parsed = JSON.parse(raw) as Record<string, unknown>
    const oauthState = typeof parsed.oauth_state === 'string' && OAUTH_STATE_PATTERN.test(parsed.oauth_state)
      ? parsed.oauth_state
      : undefined
    const method = parsed.method === 'oauth_github' || parsed.method === 'oauth_google'
      ? parsed.method
      : undefined
    const createdAt = typeof parsed.created_at === 'number' && Number.isFinite(parsed.created_at)
      ? parsed.created_at
      : undefined
    const age = createdAt === undefined ? Number.POSITIVE_INFINITY : now - createdAt
    if (
      oauthState
      && method
      && createdAt !== undefined
      && age >= -FUTURE_TOLERANCE_MS
      && age <= MAX_AGE_MS
    ) {
      return { oauth_state: oauthState, method, created_at: createdAt }
    }
  }
  catch {
    // Invalid or blocked storage is handled by the cleanup below.
  }
  clearDeferredOAuthLoginStarted()
  return undefined
}

/** Store one bounded OAuth-start intent; this is not a generic event queue. */
export function deferOAuthLoginStarted(
  oauthState: string,
  method: OAuthAnalyticsMethod,
  consent: boolean | null,
  now = Date.now(),
): boolean {
  if (consent !== true || !OAUTH_STATE_PATTERN.test(oauthState)) {
    clearDeferredOAuthLoginStarted()
    return false
  }
  const storage = sessionStorageOrUndefined()
  if (!storage)
    return false
  try {
    storage.setItem(STORAGE_KEY, JSON.stringify({ oauth_state: oauthState, method, created_at: now }))
    return true
  }
  catch {
    return false
  }
}

export function takeDeferredOAuthLoginStarted(
  oauthState: string,
  expectedMethod: OAuthAnalyticsMethod,
  consent: boolean | null,
  now = Date.now(),
): DeferredOAuthStartIntent | undefined {
  if (consent !== true) {
    clearDeferredOAuthLoginStarted()
    return undefined
  }
  const intent = readIntent(now)
  clearDeferredOAuthLoginStarted()
  return intent?.oauth_state === oauthState && intent.method === expectedMethod
    ? intent
    : undefined
}

export function clearDeferredOAuthLoginStarted(): void {
  const storage = sessionStorageOrUndefined()
  try {
    storage?.removeItem(STORAGE_KEY)
  }
  catch {
    // Analytics cleanup is best-effort in restricted browser modes.
  }
}
